/**
 * Central marketing event layer.
 *
 * Sends only non-PII metadata to dataLayer so GTM can map events to
 * Google Ads, GA4, Meta, TikTok, Reddit and other ad platforms.
 */

function safeDataLayerPush(payload) {
  try {
    if (typeof window === 'undefined') return;
    const ts = new Date().toISOString();
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event_timestamp: ts,
      ...payload,
    });

    window.__iv1MarketingEvents = window.__iv1MarketingEvents || [];
    window.__iv1MarketingEvents.push({
      ...payload,
      event_timestamp: ts,
    });
  } catch (_) {}
}

function cleanPath(pathname) {
  if (!pathname || typeof pathname !== 'string') return '/';
  return pathname.split('?')[0].split('#')[0] || '/';
}

export function trackMarketingPageView({ pathname, section, language } = {}) {
  safeDataLayerPush({
    event: 'iv1_page_view',
    page_path: cleanPath(pathname || (typeof window !== 'undefined' ? window.location.pathname : '/')),
    section: section || null,
    language: language || null,
  });
}

export function trackMarketingSignUp({ method = 'email' } = {}) {
  safeDataLayerPush({
    event: 'iv1_sign_up',
    method,
  });
}

export function trackMarketingLead({ source = 'app' } = {}) {
  safeDataLayerPush({
    event: 'iv1_lead',
    lead_source: source,
  });
}

export function trackMarketingViewPlans() {
  safeDataLayerPush({
    event: 'iv1_view_plans',
    page_path: '/plans',
  });
}

export function trackMarketingStartTrial() {
  safeDataLayerPush({
    event: 'iv1_start_trial',
    plan: 'trial',
    value: 0,
    currency: 'USD',
  });
}

export function trackMarketingInitiateCheckout(planKey) {
  const normalizedPlan = planKey === 'pro_plus' ? 'pro_plus' : 'pro';
  safeDataLayerPush({
    event: 'iv1_initiate_checkout',
    plan: normalizedPlan,
    value: normalizedPlan === 'pro_plus' ? 9.79 : 5.79,
    currency: 'USD',
  });
}

export function trackMarketingPurchase({ planKey, value, currency = 'USD' } = {}) {
  const normalizedPlan = planKey === 'pro_plus' ? 'pro_plus' : 'pro';
  safeDataLayerPush({
    event: 'iv1_purchase',
    plan: normalizedPlan,
    value: Number.isFinite(value) ? value : (normalizedPlan === 'pro_plus' ? 9.79 : 5.79),
    currency,
  });
}

export function trackDemoLandingView({ demo, language } = {}) {
  safeDataLayerPush({
    event: demo === 'deep' ? 'iv1_demo_deep_landing_view' : 'iv1_demo_study_landing_view',
    demo,
    language: language || null,
  });
}

export function trackDemoStart({ demo, language } = {}) {
  safeDataLayerPush({
    event: demo === 'deep' ? 'iv1_demo_deep_start' : 'iv1_demo_study_start',
    demo,
    language: language || null,
  });
}

export function trackDemoResult({ demo, language, sourceCount = 0 } = {}) {
  safeDataLayerPush({
    event: demo === 'deep' ? 'iv1_demo_deep_result' : 'iv1_demo_study_result',
    demo,
    language: language || null,
    source_count: sourceCount,
  });
}

export function trackDemoGateShown({ demo, action, language } = {}) {
  safeDataLayerPush({
    event: 'iv1_signup_gate_shown',
    demo,
    gated_action: action || null,
    language: language || null,
  });
}

export function trackMarketingGoogleSignupClick({ source = 'app', language } = {}) {
  safeDataLayerPush({
    event: 'iv1_google_signup_click',
    lead_source: source,
    language: language || null,
  });
}

export function trackDemoGoogleLoginSuccess({ demo, language } = {}) {
  safeDataLayerPush({
    event: 'iv1_demo_google_login_success',
    demo,
    language: language || null,
  });
}

export function trackDemoResumeRestored({ demo, action, language } = {}) {
  safeDataLayerPush({
    event: 'iv1_demo_resume_restored',
    demo,
    gated_action: action || null,
    language: language || null,
  });
}
